import { QuoteFormData, quoteFormSchema } from "@/lib/validations/quote-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import axios from "axios"

const TOTAL_STEPS = 3 // Steps: 0 (Move Details), 1 (Contact Info), 2 (Final Details)

export function useQuoteForm() {
  const [currentStep, setCurrentStep] = useState(0)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<QuoteFormData>({
    resolver: zodResolver(quoteFormSchema),
    mode: "onBlur",
    defaultValues: {
      from: "",
      to: "",
      moveType: "household",
      name: "",
      email: "",
      phone: "",
      moveDate: undefined,
      inventory: [],
      message: "",
    },
  })

  const getStepFields = (step: number): (keyof QuoteFormData)[] => {
    switch (step) {
      case 0:
        return ["from", "to", "moveType"]
      case 1:
        return ["name", "email", "phone"]
      case 2:
        return ["message"]
      default:
        return []
    }
  }

  const handleNext = async () => {
    if (currentStep >= TOTAL_STEPS - 1) return; // <-- guard

    const fieldsToValidate = getStepFields(currentStep)
    const isValid = await form.trigger(fieldsToValidate)

    if (isValid) {
      setCurrentStep(prev => prev + 1)
      setSubmitError(null)
    }
  }


  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
      setSubmitError(null)
    }
  }

  const onSubmit = async (data: QuoteFormData) => {
    // Only submit if we're on the last step
    if (currentStep !== TOTAL_STEPS - 1) {
      console.warn('⚠️ Prevented submission - not on last step!')
      return
    }
    
    setSubmitError(null)
    
    try {
      // Submit to API
      await axios.post('/api/quote', {
        name: data.name,
        email: data.email,
        phone: data.phone,
        from_city: data.from,
        to_city: data.to,
        move_type: data.moveType,
        moving_date: data.moveDate ? new Date(data.moveDate).toISOString() : new Date().toISOString(),
        inventory: data.inventory || [],
        message: data.message || undefined,
        status: 'new'
      })

      setIsSubmitted(true)
    } catch (error) {
      console.error('Error submitting quote:', error)
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'There was an error submitting your quote. Please try again.'
      )
    }
  }

  const isStepValid = (step: number) => {
    const fields = getStepFields(step)

    // For the last step (step 2), message is optional, so always return true if no errors
    if (step === 2) {
      return !form.formState.errors.message
    }

    // For other steps, check that all fields have values and no errors
    return fields.every(field => {
      const value = form.getValues(field)
      return value !== "" && value !== undefined && !form.formState.errors[field]
    })
  }

  return {
    form,
    currentStep,
    isSubmitted,
    submitError,
    handleNext,
    handlePrevious,
    onSubmit,
    isStepValid,
  }
}
